/**
 * Per-device dismissal state for the pop-up banner (FE-14 / story AC-15).
 *
 * Keyed by the Strapi `documentId` the endpoint returns. Written only on interaction — closing or
 * following a CTA — never on display, and never when the artwork failed to load (FE-17). The list
 * is pruned against the ids the endpoint currently serves so it cannot grow without bound.
 *
 * Known limitation, accepted for this release: localStorage is per device, so AC-15 holds per
 * device rather than per user.
 */
export const POPUP_BANNER_DISMISSED_KEY = 'popupBanner:dismissed';

const read = (): string[] => {
  try {
    const raw = localStorage.getItem(POPUP_BANNER_DISMISSED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const write = (ids: string[]): void => {
  try {
    localStorage.setItem(POPUP_BANNER_DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable — the banner degrades to reappearing, never to throwing.
  }
};

export const getDismissedBannerIds = (): string[] => read();

export const isBannerDismissed = (id: string): boolean => read().includes(id);

/** Idempotent: recording the same id twice leaves one entry. */
export const markBannerDismissed = (id: string): void => {
  const ids = read();
  if (ids.includes(id)) return;
  write([...ids, id]);
};

/**
 * Drop stored ids the endpoint no longer serves. `servedIds` is what the current response carries,
 * so an empty response clears the list.
 */
export const pruneDismissedBanners = (servedIds: readonly string[]): void => {
  const ids = read();
  const kept = ids.filter((id) => servedIds.includes(id));
  if (kept.length !== ids.length) write(kept);
};

export const clearDismissedBanners = (): void => {
  try {
    localStorage.removeItem(POPUP_BANNER_DISMISSED_KEY);
  } catch {
    // Nothing to clear.
  }
};
